import React, {useCallback, useEffect, useMemo, useRef} from "react";
import {useNavigate} from "react-router-dom";
import Box from "@mui/material/Box";
import {useAuth} from "../../../hooks/useAuth";
import useDataTable from "../../../hooks/useDataTable";
import CustomDataTable from "../../../shared/tables/CustomDataTable";
import GenericButton from "../../../shared/ui/GenericButton";
import StyledLink from "../../../shared/ui/LinkStyled";
import Icon from "../../../shared/ui/Icon";
import {arrayEquals, getRomanNumber, niceUserName} from "../../../utils/functions";
import {APIGetAllClassrooms} from "../../classrooms/api/classrooms";
import {APIGetUsers} from "../api/users";
import {APIArchiveUser, APIRestoreUser, APIUpdateUserClassrooms} from "../api/user";
import AddClassroomsDialog, {toClassroomOption} from "./AddClassroomsDialog";
import {IUserPage} from "../../../shared/types/Users";
import {IClassroom, IClassroomOption} from "../../../shared/types/Classroom";

type UserType = "students" | "teachers";

type UserAction = "archive" | "restore";

export const USERS = [
    {id: "last_name", label: "Nume și prenume", noWrap: true, sortable: true, width: "180px", tooltip: true, tooltipTextLength: 21},
    {id: "email", label: "E-mail", sortable: true,},
    {id: "classrooms", label: "Clasă", noWrap: true, sortable: true, width: "180px", tooltip: true, tooltipTextLength: 21},
    {id: "email_verified_at", label: "Status", noWrap: true, disablePadding: true, sortable: true,},
    {id: "actions", label: "Acțiuni", sticky: true, numeric: true},
];

const USER_TYPE_CONFIG = {
    students: {
        basePath: "/elevi",
        activeSection: "Elevi activi",
        archivedSection: "Elevi arhivați",
        labelSingular: "Elevul",
        titleName: "elev",
    },
    teachers: {
        basePath: "/profesori",
        activeSection: "Profesori activi",
        archivedSection: "Profesori arhivați",
        labelSingular: "Profesorul",
        titleName: "profesor",
    },
};


const actionsConfig = (type: UserType, user: IUserPage, action: "archive" | "restore") => {
    const config = USER_TYPE_CONFIG[type];

    if(action === "archive") {
        return {
            api: APIArchiveUser,
            buttonYes: "Arhivează",
            title: `Ești sigur că vrei să arhivezi acest ${config.titleName}?`,
            message: (
                <>
                    {config.labelSingular}
                    <b> {niceUserName(user.first_name, user.last_name, user.email)}</b> va fi arhivat.
                    Veți regăsi acest utilizator în secțiunea <b>{config.archivedSection}</b>.
                </>)
        }
    } else {
        return {
            api: APIRestoreUser,
            buttonYes: "Dezarhivează",
            title: `Ești sigur că vrei să dezarchivezi acest ${config.titleName}?`,
            message: (
                <>
                    {config.labelSingular}
                    <b> {niceUserName(user.first_name, user.last_name, user.email)}</b> va fi dezarchivat.
                    Veți regăsi acest utilizator în secțiunea <b>{config.activeSection}</b>.
                </>
            )
        }
    }
}

const Users = ({type, isArchived} : {type: UserType, isArchived: boolean}) => {
    const {update, tenant} = useAuth();
    const navigate = useNavigate();
    const typeConfig = USER_TYPE_CONFIG[type];

    const {
        loading,
        setLoading,
        tableData,
        setTableData,
        totalRows,
        setTotalRows,
        perPage,
        setPerPage,
        page,
        setPage,
        query,
        submittedQuery,
        orderBy,
        order,
        handleChangePage,
        handleRequestSort,
        handleSearch,
        handleSearchChange,
        handleClearSearch,
    } = useDataTable("", "last_name", undefined, "");

    const pageRef = useRef(page);

    useEffect(() => {
        pageRef.current = page;
    }, [page]);

    const renderUserLink = (activeUser: IUserPage) => (
        <StyledLink to={`${typeConfig.basePath}/${activeUser.id}`}>
            {niceUserName(activeUser.first_name, activeUser.last_name, activeUser.email)}
        </StyledLink>
    );

    const renderClassrooms = (classrooms?: IClassroom[]) => {
        if (!classrooms?.length) return "";

        return (classrooms.map((classroom) => (
            <StyledLink key={classroom.id} to={`/clase/${classroom.id}`}  sx={{display: "inline-block", mr: 1}}>
                ({getRomanNumber(classroom.grade)}) {classroom.name}
            </StyledLink>
        )));
    };

    const renderEmailVerification = (emailVerifiedAt: string | null) => (
        <Icon
            icon={emailVerifiedAt !== null ? "fas circle-check" : "fas circle-xmark"}
            fixedWidth={true}
            sx={{
                fontSize: "1.5rem",
                color: emailVerifiedAt !== null ? "success.main" : "error.main",
            }}
        />
    );

    const getActions = (activeUser: IUserPage) => (
        <Box display="flex" justifyContent="end">
            {!isArchived && (
                <Box mr={1}>
                    <GenericButton type="add-classrooms" onClick={() => confirmAddClassrooms(activeUser)} disabled={activeUser.parent_id === null}/>
                </Box>
            )}
            <Box mr={1}>
                <GenericButton
                    type="edit-small"
                    onClick={() => {
                        editUserHandler(activeUser.id);
                    }}
                    disabled={activeUser.parent_id === null}
                />
            </Box>
            <Box>
                {isArchived ? (
                    <GenericButton type="restore" onClick={() => confirmUserAction("restore", activeUser)} disabled={activeUser.parent_id === null}/>
                ) : (
                    <GenericButton type="archive" onClick={() => confirmUserAction("archive", activeUser)} disabled={activeUser.parent_id === null}/>
                )}
            </Box>
        </Box>
    );

    const mapUserToRow = useCallback((activeUser: IUserPage) => {
        return {
            last_name: renderUserLink(activeUser),
            email: activeUser.email,
            classrooms: renderClassrooms(activeUser.classrooms),
            email_verified_at: renderEmailVerification(activeUser.email_verified_at),
            actions: getActions(activeUser),
        }
    }, []);

    const getUsers: any = useCallback(async (currentPage = pageRef.current) => {
        try {
            const response = await APIGetUsers({
                page: `?page=${currentPage + 1}`,
                sort: orderBy,
                order,
                search: submittedQuery,
                type,
                isArchived,
            });

            if (!response.data.success) {
                update("error", {show: true, code: "A002"});
                return;
            }

            const pageData = response.data.data;

            if (pageData.data.length === 0 && currentPage > 0) {
                const previousPage = currentPage - 1;

                setPage(previousPage);
                await getUsers(previousPage);
                return;
            }

            setTableData(pageData.data.map((user: IUserPage) => mapUserToRow(user)));
            setTotalRows(pageData.total);
            setPerPage(pageData.per_page);
        } catch {
            update("error", {show: true, code: "A001"});
        } finally {
            setLoading(false);
        }
    }, [order, submittedQuery, type, isArchived, mapUserToRow, setTableData, setTotalRows, setPerPage, update, setLoading, setPage]);

    useEffect(() => {
        setLoading(true);
        getUsers();
    }, [page, orderBy, order, submittedQuery]);

    const editUserHandler = useCallback(
        (id: number) => {
            navigate(`${typeConfig.basePath}/${id}/editare`);
        }, [navigate]);

    const updateUser = (
        action: UserAction,
        activeUser: IUserPage,
        callback: () => void,
        ) => {
        update("loading", { show: true, modal: true });
        const config = actionsConfig(type, activeUser, action);

        config.api({id: activeUser.id, type: type})
            .then((response) => {
                if (response.data.success) {
                    getUsers(pageRef.current);
                } else update("error", { show: true, code: "A002" })
                callback();
            })
            .catch(() => update("error", { show: true, code: "A001" }))
            .finally(() => { update("loading", { show: false });})
    }

    const confirmUserAction = (
        action: UserAction,
        activeUser: IUserPage
    ) => {
        const config = actionsConfig(type, activeUser, action);

        update("confirm", {
            show: true,
            closable: true,
            icon: "fat box-archive",
            title: config.title,
            message: config.message,
            returnFunction: (callback: () => void) => {
                updateUser(action, activeUser, callback);
            },
            buttonYes: config.buttonYes,
            buttonNo: "Închide",
        });
    }

    const addClassrooms = (user: IUserPage, classrooms: IClassroom[], callback: () => void) => {
        const currentClassrooms = user.classrooms.map(({ id }: any) => id);
        const newClassrooms = classrooms.map(({ id }: any) => id);

        if (newClassrooms.length === 0 || arrayEquals(newClassrooms, currentClassrooms)) {
            return;
        }

        update("loading", { show: true, modal: true });
        APIUpdateUserClassrooms(user.id, type, {classrooms: newClassrooms })
            .then((response) => {
                if (response.data.success) {
                    // console.log("updateClassroom OK", response);
                    getUsers(pageRef.current);
                } else update("error", { show: true, code: "A002" });
            })
            .catch(() => {
                update("error", { show: true, code: "A001" });
            })
            .finally(() => {
                callback();
                update("loading", {show: false});
            });
    };

    const confirmAddClassrooms = (user: IUserPage) => {
        const selectedClassroomsRef = {
            current: (user.classrooms ?? []).map(toClassroomOption),
        };

        APIGetAllClassrooms()
            .then((response) => {
                if (response.data.success) {
                    // console.log("getClassroom OK", response);
                    const classrooms: IClassroomOption[] = response.data.data.items.map(toClassroomOption);

                    update("confirm", {
                        show: true,
                        closable: true,
                        icon: "fat screen-users",
                        title: "Adaugă clasă",
                        size: "large",
                        message: (
                            <AddClassroomsDialog
                                classrooms={classrooms}
                                selectedClassrooms={selectedClassroomsRef.current}
                                loading={loading}
                                onChange={(nextClassrooms) => {
                                    selectedClassroomsRef.current = nextClassrooms;
                                }}
                            />
                        ),
                        returnFunction: (callback: () => void) => {
                            addClassrooms(
                                user,
                                selectedClassroomsRef.current,
                                callback,
                            );
                        },
                        buttonYes: "Adaugă",
                        buttonNo: "Închide",
                    });

                } else {
                    update("error", { show: true, code: "A002" });
                }
            })
            .catch(() => {
                // console.log("getClassroom ERR", err);
                update("error", { show: true, code: "A001" });
            });
    };

    const exportConfig = useMemo(() => ({
        buildRequestParams: () => ({
            page: `?page=${page + 1}`,
            sort: orderBy,
            order: order,
            search: submittedQuery,
            type: type,
            export: true,
        }),
        fetchData: async (params: any) => {
            const response = await APIGetUsers(params);
            if (!response.data.success || !response.data.data) {
                return [];
            }
            return response.data.data.values;
        },
        mapRow: (item: IUserPage) => {
            return {
                Id: item.id,
                Email: item.email,
                Nume: item.last_name,
                Prenume: item.first_name,
                "Clasa": item.classrooms,
            };
        },
        buildFileName: () => `${type === "teachers" ? "profesori" : "elevi"}-${tenant.name}.xlsx`,
    }), [order, page, submittedQuery, type, tenant.name]);


    return (
        <CustomDataTable
            loading={loading}
            columns={USERS}
            rows={tableData}
            rowsPerPage={perPage}
            page={page}
            totalRows={totalRows}
            onPageChange={handleChangePage}
            handleRequestSort={handleRequestSort}
            orderBy={orderBy}
            order={order}
            hasSearch={true}
            onChange={handleSearchChange}
            onSearch={handleSearch}
            searchValue={query}
            onClearSearch={handleClearSearch}
            hasExport={!isArchived}
            exportData={exportConfig}
        />
    )
}

export default Users;